import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradeLockCard } from '../components/UpgradeLock';
import { Sparkline, Donut, HBars, VelocityBars, ThroughputChart, projectPaletteFor } from '../components/Charts';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import AppFooter from '../components/AppFooter';
import {
  ChartBar, TrendUp, Warning, Clock, Lightning, CheckCircle, Trophy, ArrowsClockwise,
  ArrowDown, ArrowUp, Minus, ChartLineUp, Hourglass, Stack, Crown, FlagBanner, FolderOpen,
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  todo: '#a1a1aa', in_progress: '#3b82f6', review: '#f59e0b', done: '#10b981',
};
const STATUS_LABELS = {
  todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done',
};
const PRIORITY_COLORS = {
  critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#71717a',
};
const TYPE_COLORS = {
  story: '#10b981', bug: '#ef4444', task: '#3b82f6',
};

// Compact KPI card with sparkline + delta arrow.
function KPI({ icon: Icon, color, label, value, sub, sparkData, delta, tone, testid, onClick }) {
  const palette = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', spark: '#10b981' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', spark: '#f59e0b' },
    red: { bg: 'bg-red-50', text: 'text-red-600', spark: '#ef4444' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-600', spark: '#8b5cf6' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', spark: '#3b82f6' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', spark: '#6366f1' },
  }[color] || { bg: 'bg-zinc-100', text: 'text-zinc-600', spark: '#71717a' };

  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white rounded-2xl border border-zinc-200/70 p-5 group transition-all duration-200 ${onClick ? 'hover:border-zinc-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-bold">{label}</span>
        <div className={`w-8 h-8 rounded-lg ${palette.bg} ${palette.text} flex items-center justify-center transition-transform group-hover:scale-110`}>
          <Icon size={15} weight="bold" />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <p className={`font-['Outfit'] text-[30px] font-black tracking-tight ${tone === 'bad' ? 'text-red-600' : 'text-zinc-900'} tabular-nums`}>{value}</p>
        {delta != null && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${
            delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-zinc-400'
          }`}>
            {delta > 0 ? <ArrowUp size={10} weight="bold" /> : delta < 0 ? <ArrowDown size={10} weight="bold" /> : <Minus size={10} weight="bold" />}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
      {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
      {sparkData?.length > 0 && (
        <div className="mt-3 -mx-1">
          <Sparkline values={sparkData} color={palette.spark} height={28} />
        </div>
      )}
    </Wrap>
  );
}

function Section({ title, subtitle, icon: Icon, action, children, testid, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-zinc-200/70 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={15} className="text-zinc-500" weight="bold" />}
          <div>
            <h3 className="text-[13px] font-bold text-zinc-800 tracking-tight">{title}</h3>
            {subtitle && <p className="text-[10px] text-zinc-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function WorkloadRow({ row, max }) {
  const navigate = useNavigate();
  const pct = max > 0 ? (row.total / max) * 100 : 0;
  const inProgressPct = max > 0 ? (row.in_progress / max) * 100 : 0;
  const overduePct = max > 0 ? (row.overdue / max) * 100 : 0;
  return (
    <button
      onClick={() => navigate('/insights')}
      className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-zinc-50 transition-colors group text-left"
      data-testid={`workload-${row.user?.user_id}`}
    >
      <Avatar className="h-8 w-8 flex-shrink-0">
        {row.user?.avatar && <AvatarImage src={row.user.avatar} />}
        <AvatarFallback className={`${getAvatarStyle(row.user?.user_id || row.user?.name).bg} text-white text-[10px] font-bold`}>{getInitials(row.user?.name)}</AvatarFallback>
      </Avatar>
      <span className="text-[12px] text-zinc-700 w-32 truncate flex-shrink-0 font-medium">{row.user?.name || 'Unknown'}</span>
      <div className="flex-1 h-6 relative bg-zinc-100/80 rounded-md overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-blue-200 transition-all" style={{ width: `${pct}%` }} />
        <div className="absolute inset-y-0 left-0 bg-blue-500 transition-all" style={{ width: `${inProgressPct}%` }} />
        {overduePct > 0 && <div className="absolute inset-y-0 right-0 bg-red-500 transition-all" style={{ width: `${overduePct}%` }} />}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-zinc-500 tabular-nums flex-shrink-0 w-32 justify-end">
        <span className="text-zinc-800 font-bold text-[12px]">{row.total}</span>
        <span className="text-zinc-400">·</span>
        <span>{row.in_progress} in prog</span>
        {row.overdue > 0 && <span className="text-red-600 font-semibold">· {row.overdue} od</span>}
      </div>
    </button>
  );
}

export default function InsightsPage() {
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const { hasCap, loading: subLoading } = useSubscription();
  // Insights is a leadership view — owner / admin / project_manager only.
  // If a contributor or viewer types the URL directly, bounce them home so
  // the page never even paints. Backend endpoints also return scoped /
  // empty payloads so no data leaks either way.
  useEffect(() => {
    if (currentOrg && !['owner', 'admin', 'project_manager'].includes(currentOrg.role)) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentOrg, navigate]);
  const [summary, setSummary] = useState(null);
  const [velocity, setVelocity] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [throughput, setThroughput] = useState([]);
  const [distribution, setDistribution] = useState({ by_status: [], by_priority: [], by_work_type: [] });
  const [cycleTime, setCycleTime] = useState(null);
  const [leaderboard, setLeaderboard] = useState({ rows: [] });
  const [projectHealth, setProjectHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [windowDays, setWindowDays] = useState('60');
  const [selectedProject, setSelectedProject] = useState('all'); // 'all' | project_id

  const load = useCallback(async () => {
    if (!currentOrg || !hasCap('advanced_insights')) { setLoading(false); return; }
    setLoading(true);
    try {
      const h = { withCredentials: true, headers: getOrgHeaders() };
      const pq = selectedProject !== 'all' ? `&project_id=${selectedProject}` : '';
      const [s, v, w, t, d, c, lb, ph] = await Promise.all([
        axios.get(`${API}/insights/summary?${pq.slice(1) || '_=1'}`, h),
        axios.get(`${API}/insights/velocity?limit=8${pq}`, h),
        axios.get(`${API}/insights/workload?${pq.slice(1) || '_=1'}`, h),
        axios.get(`${API}/insights/throughput?weeks=8${pq}`, h),
        axios.get(`${API}/insights/distribution?${pq.slice(1) || '_=1'}`, h),
        axios.get(`${API}/insights/cycle-time?days=${windowDays}${pq}`, h),
        axios.get(`${API}/insights/leaderboard?days=${windowDays}${pq}`, h),
        // Project-health is always full org-wide so the user can compare projects side-by-side.
        axios.get(`${API}/insights/project-health`, h),
      ]);
      setSummary(s.data); setVelocity(v.data); setWorkload(w.data);
      setThroughput(t.data?.weeks || []);
      setDistribution(d.data || { by_status: [], by_priority: [], by_work_type: [] });
      setCycleTime(c.data); setLeaderboard(lb.data || { rows: [] });
      setProjectHealth(ph.data?.projects || []);
      setRefreshedAt(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, currentOrg, getOrgHeaders, hasCap, windowDays, selectedProject]);

  useEffect(() => { load(); }, [load]);

  const maxWorkload = useMemo(() => Math.max(1, ...workload.map(r => r.total)), [workload]);

  const avgVelocity = useMemo(() => {
    const done = velocity.filter(v => v.status === 'completed').map(v => v.completed_points || 0);
    if (!done.length) return null;
    return Math.round(done.reduce((a, b) => a + b, 0) / done.length);
  }, [velocity]);

  // Sparkline series for KPIs (derived from throughput + velocity history).
  const completedSpark = throughput.map(w => w.completed || 0);
  const velocitySpark = velocity.map(v => v.completed_points || 0);

  // Status & priority sorted into stable order.
  const statusSegments = ['todo', 'in_progress', 'review', 'done']
    .map(k => ({
      key: k, label: STATUS_LABELS[k] || k, color: STATUS_COLORS[k] || '#71717a',
      value: distribution.by_status.find(x => x.key === k)?.count || 0,
    }))
    .filter(s => s.value > 0);
  const totalTasks = distribution.by_status.reduce((a, b) => a + b.count, 0);
  const donePct = totalTasks > 0 ? Math.round(((distribution.by_status.find(x => x.key === 'done')?.count || 0) / totalTasks) * 100) : 0;

  const priorityRows = ['critical', 'high', 'medium', 'low']
    .map(k => ({ key: k, label: k, value: distribution.by_priority.find(x => x.key === k)?.count || 0 }))
    .filter(r => r.value > 0);
  const typeRows = ['story', 'bug', 'task']
    .map(k => ({ key: k, label: k, value: distribution.by_work_type.find(x => x.key === k)?.count || 0 }))
    .filter(r => r.value > 0);

  // Color palette for project-health rows (and used by velocity legend).
  const projectColorMap = useMemo(() => {
    const palette = projectPaletteFor(projectHealth.map(r => r.project));
    return Object.fromEntries(palette.map(p => [p.project.project_id, p.dark]));
  }, [projectHealth]);

  if (!subLoading && !hasCap('advanced_insights')) {
    return (
      <UpgradeLockCard
        title="Advanced Insights is a Business plan feature"
        description="Upgrade to unlock workspace-wide velocity, workload, throughput, cycle time, and leaderboards."
        featureName="advanced_insights"
        testId="insights-upgrade-lock"
      />
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-gradient-to-b from-zinc-50/60 to-white" data-testid="insights-page">
      {/* Hero header with gradient */}
      <div className="relative bg-gradient-to-br from-indigo-700 via-violet-700 to-purple-800 px-8 py-8 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_50%)]" />
        <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-violet-200 font-bold mb-1.5 inline-flex items-center gap-1.5">
                <ChartLineUp size={12} weight="bold" /> Workspace insights
              </p>
              <h1 className="font-['Outfit'] text-[28px] font-black tracking-tight leading-tight">
                Performance & Health
              </h1>
              <p className="text-[12px] text-violet-100/80 mt-1.5">
                {currentOrg?.org_name} · {refreshedAt ? `last refreshed ${refreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'loading…'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={windowDays} onValueChange={setWindowDays}>
                <SelectTrigger className="h-8 w-32 bg-white/10 border-white/20 text-white text-[11px] hover:bg-white/15" data-testid="insights-window-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                className="h-8 text-[11px] bg-white/10 hover:bg-white/20 text-white" data-testid="insights-refresh">
                <ArrowsClockwise size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {/* Project filter chip bar — scopes Velocity / Throughput / Distribution / Cycle / Workload / Leaderboard.
              Project Health scorecard always stays org-wide so users can scan all projects at once. */}
          {projectHealth.length > 1 && (
            <div className="flex items-center gap-2 mt-5 pb-1 overflow-x-auto" data-testid="insights-project-filter">
              <span className="text-[10px] uppercase tracking-wider text-violet-200 font-bold flex-shrink-0">Filter:</span>
              <button
                onClick={() => setSelectedProject('all')}
                data-testid="proj-filter-all"
                className={`flex-shrink-0 px-3 h-7 rounded-full text-[11px] font-bold transition-all border ${
                  selectedProject === 'all'
                    ? 'bg-white text-violet-700 border-white shadow-sm'
                    : 'bg-white/5 text-white border-white/20 hover:bg-white/10'
                }`}
              >
                All projects · {projectHealth.length}
              </button>
              {projectHealth.map(r => (
                <button
                  key={r.project.project_id}
                  onClick={() => setSelectedProject(r.project.project_id)}
                  data-testid={`proj-filter-${r.project.project_id}`}
                  className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[11px] font-bold transition-all border ${
                    selectedProject === r.project.project_id
                      ? 'bg-white text-violet-700 border-white shadow-sm'
                      : 'bg-white/5 text-white border-white/20 hover:bg-white/10'
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: projectColorMap[r.project.project_id] || '#a78bfa' }} />
                  <span className="font-mono text-[10px] opacity-80">{r.project.key}</span>
                  <span>{r.project.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-8 py-6 max-w-[1400px] mx-auto -mt-3 space-y-5">
        {/* Project Health scorecard — answers "are any of my projects in trouble?" at a glance.
            When a project filter is active, the scorecard collapses to that one project.
            Click a row to toggle the project filter chip above. */}
        {projectHealth.length > 0 && (
          <Section title="Project health"
            subtitle={selectedProject === 'all' ? 'One row per project · click to filter dashboard' : 'Filtered to selected project · click row to clear'}
            icon={FolderOpen}
            action={<span className="text-[11px] text-zinc-400">{selectedProject === 'all' ? `${projectHealth.length} project${projectHealth.length !== 1 ? 's' : ''}` : '1 of ' + projectHealth.length}</span>}
            testid="insights-project-health"
          >
            <div className="overflow-x-auto -mx-1">
              <table className="w-full min-w-[720px] text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider font-bold text-zinc-400 border-b border-zinc-100">
                    <th className="px-2 py-2 font-bold">Project</th>
                    <th className="px-2 py-2 font-bold w-32">Completion</th>
                    <th className="px-2 py-2 font-bold w-20 text-center">Active</th>
                    <th className="px-2 py-2 font-bold w-20 text-center">Overdue</th>
                    <th className="px-2 py-2 font-bold w-24 text-center">Done 7d</th>
                    <th className="px-2 py-2 font-bold w-48">Active sprint</th>
                    <th className="px-2 py-2 font-bold w-20 text-center">Health</th>
                  </tr>
                </thead>
                <tbody>
                  {projectHealth
                    .filter(r => selectedProject === 'all' || r.project.project_id === selectedProject)
                    .map(r => {
                    const healthChip = {
                      healthy: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Healthy' },
                      warning: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Warning' },
                      at_risk: { bg: 'bg-red-100', text: 'text-red-700', label: 'At risk' },
                    }[r.health];
                    const sp = r.active_sprint;
                    const dayTone = sp?.days_left == null ? 'text-zinc-500'
                      : sp.days_left < 2 ? 'text-red-600'
                      : sp.days_left < 5 ? 'text-amber-600' : 'text-zinc-500';
                    const isSelected = selectedProject === r.project.project_id;
                    return (
                      <tr
                        key={r.project.project_id}
                        onClick={() => setSelectedProject(isSelected ? 'all' : r.project.project_id)}
                        className={`border-b border-zinc-50 hover:bg-zinc-50/70 cursor-pointer transition-colors ${isSelected ? 'bg-violet-50/40' : ''}`}
                        data-testid={`project-health-row-${r.project.project_id}`}
                      >
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-1 self-stretch rounded-full" style={{ backgroundColor: projectColorMap[r.project.project_id] || '#a78bfa' }} />
                            <span className="font-mono text-[10px] text-zinc-400 flex-shrink-0">{r.project.key}</span>
                            <span className="font-bold text-zinc-800 truncate">{r.project.name}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${r.completion_pct}%` }} />
                            </div>
                            <span className="text-[11px] font-bold text-zinc-700 tabular-nums w-9 text-right">{r.completion_pct}%</span>
                          </div>
                          <p className="text-[10px] text-zinc-400 mt-0.5 tabular-nums">{r.done_tasks} / {r.total_tasks}</p>
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <span className="font-bold text-blue-600 tabular-nums">{r.in_progress_tasks}</span>
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <span className={`font-bold tabular-nums ${r.overdue_tasks > 0 ? 'text-red-600' : 'text-zinc-300'}`}>{r.overdue_tasks}</span>
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <span className={`font-bold tabular-nums ${r.completed_7d > 0 ? 'text-emerald-600' : 'text-zinc-300'}`}>{r.completed_7d}</span>
                        </td>
                        <td className="px-2 py-2.5">
                          {sp ? (
                            <div>
                              <p className="text-[11px] font-semibold text-zinc-700 truncate">{sp.name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <div className="flex-1 h-1 bg-zinc-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${sp.progress_pct}%` }} />
                                </div>
                                <span className="text-[9px] text-zinc-500 tabular-nums">{sp.progress_pct}%</span>
                                <span className={`text-[9px] font-bold tabular-nums ${dayTone}`}>
                                  {sp.days_left != null ? (sp.days_left > 0 ? `${sp.days_left}d` : sp.days_left === 0 ? 'today' : 'ended') : ''}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-[11px] text-zinc-400 italic">No active sprint</span>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${healthChip.bg} ${healthChip.text}`}>{healthChip.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* KPI strip */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="insights-summary">
            <KPI icon={CheckCircle} color="emerald" label="Completion rate" value={`${summary.completion_rate}%`}
              sub={`${summary.done_tasks} of ${summary.total_tasks} done`} sparkData={completedSpark} testid="kpi-completion" />
            <KPI icon={Lightning} color="blue" label="In progress" value={summary.in_progress_tasks}
              sub="active right now" testid="kpi-in-progress" />
            <KPI icon={Warning} color="red" label="Overdue" value={summary.overdue_tasks}
              sub="past due, not done" tone={summary.overdue_tasks > 0 ? 'bad' : 'ok'} testid="kpi-overdue" />
            <KPI icon={TrendUp} color="violet" label="Avg velocity" value={avgVelocity ?? '—'}
              sub="SP per completed sprint" sparkData={velocitySpark} testid="kpi-velocity" />
          </div>
        )}

        {/* Velocity + Throughput side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="Sprint velocity" subtitle="Committed vs completed (Story Points)" icon={TrendUp}
            action={<span className="text-[11px] text-zinc-400">Last {velocity.length} sprint{velocity.length !== 1 ? 's' : ''}</span>}
            testid="insights-velocity"
          >
            <VelocityBars data={velocity} colorByProject={selectedProject === 'all'} />
            {selectedProject === 'all' ? (
              // Per-project legend so admins can read the color-coded bars.
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 pt-3 border-t border-zinc-100">
                {[...new Map(velocity.filter(v => v.project).map(v => [v.project.project_id, v.project])).values()].map((p) => (
                  <span key={p.project_id} className="inline-flex items-center gap-1 text-[10px] text-zinc-600">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: projectColorMap[p.project_id] || '#4f46e5' }} />
                    <span className="font-mono text-[9px] text-zinc-400">{p.key}</span>
                    <span className="font-medium">{p.name}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center gap-5 text-[10px] text-zinc-500 mt-2 pt-3 border-t border-zinc-100">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-indigo-200" /> Committed</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-indigo-600" /> Completed</span>
              </div>
            )}
          </Section>
          <Section title="Throughput" subtitle="Tasks created vs completed" icon={ChartLineUp}
            action={<span className="text-[11px] text-zinc-400">Last 8 weeks</span>}
            testid="insights-throughput"
          >
            <ThroughputChart weeks={throughput} />
            <div className="flex items-center justify-center gap-5 text-[10px] text-zinc-500 mt-2 pt-3 border-t border-zinc-100">
              <span className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-sm bg-emerald-500" /> Completed</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded-sm bg-blue-500" style={{ borderTop: '1.5px dashed #3b82f6', height: 0 }} /> Created (dashed)</span>
            </div>
          </Section>
        </div>

        {/* Distribution + Cycle time */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Status donut */}
          <Section title="Status mix" subtitle="Where work currently sits" icon={Stack} testid="insights-status-mix">
            <div className="flex items-center justify-center py-2">
              <Donut
                segments={statusSegments.map(s => ({ value: s.value, color: s.color }))}
                size={150}
                thickness={20}
                label={`${donePct}%`}
                sublabel="Done"
              />
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-2 pt-3 border-t border-zinc-100">
              {statusSegments.map(s => (
                <div key={s.key} className="flex items-center gap-1.5 text-[11px] text-zinc-700">
                  <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="truncate">{s.label}</span>
                  <span className="text-zinc-400 tabular-nums ml-auto">{s.value}</span>
                </div>
              ))}
              {statusSegments.length === 0 && <span className="col-span-2 text-[11px] text-zinc-400 text-center py-2">No tasks yet</span>}
            </div>
          </Section>

          {/* Priority + Work-type bars */}
          <Section title="Priority" subtitle="Active work breakdown" icon={FlagBanner} testid="insights-priority-mix">
            <HBars rows={priorityRows} colorMap={PRIORITY_COLORS} />
            {priorityRows.length === 0 && <p className="text-[11px] text-zinc-400 text-center py-4">No data</p>}
            <div className="border-t border-zinc-100 mt-4 pt-3">
              <p className="text-[10px] uppercase tracking-wide font-bold text-zinc-400 mb-2">Work types</p>
              <HBars rows={typeRows} colorMap={TYPE_COLORS} height={18} />
            </div>
          </Section>

          {/* Cycle time card */}
          <Section title="Cycle time" subtitle="Days from create → done" icon={Hourglass} testid="insights-cycle-time">
            {cycleTime?.count > 0 ? (
              <>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-['Outfit'] text-[36px] font-black text-zinc-900 tabular-nums leading-none">{cycleTime.avg_days}</span>
                  <span className="text-[12px] text-zinc-500">avg days</span>
                </div>
                <p className="text-[10px] text-zinc-400">across {cycleTime.count} tasks completed in last {cycleTime.window_days}d</p>
                <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-zinc-100">
                  <div className="bg-emerald-50/60 rounded-lg p-2.5">
                    <p className="text-[9px] uppercase tracking-wide font-bold text-emerald-700">P50 (median)</p>
                    <p className="font-['Outfit'] text-[18px] font-black text-emerald-700 tabular-nums leading-none mt-0.5">{cycleTime.p50_days}d</p>
                  </div>
                  <div className="bg-amber-50/60 rounded-lg p-2.5">
                    <p className="text-[9px] uppercase tracking-wide font-bold text-amber-700">P90 (slowest)</p>
                    <p className="font-['Outfit'] text-[18px] font-black text-amber-700 tabular-nums leading-none mt-0.5">{cycleTime.p90_days}d</p>
                  </div>
                </div>
                {cycleTime.slowest?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-zinc-100">
                    <p className="text-[10px] uppercase tracking-wide font-bold text-zinc-400 mb-2">Slowest recent</p>
                    <div className="space-y-1">
                      {cycleTime.slowest.slice(0, 3).map(s => (
                        <button key={s.task_id} onClick={() => navigate(`/tasks/${s.task_id}`)}
                          className="w-full flex items-center gap-2 text-left text-[11px] text-zinc-600 hover:text-blue-700 hover:bg-blue-50/40 rounded px-1.5 py-1 transition-colors"
                          data-testid={`cycle-slowest-${s.task_id}`}>
                          <span className="font-mono text-zinc-400 text-[10px] flex-shrink-0">{s.task_key}</span>
                          <span className="truncate flex-1">{s.title}</span>
                          <span className="font-bold text-amber-600 tabular-nums">{s.days}d</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[11px] text-zinc-400 text-center py-6">No completions in this window yet.</p>
            )}
          </Section>
        </div>

        {/* Workload + Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Section title="Workload by assignee" subtitle="Active tasks · in progress · overdue" icon={ChartBar}
            action={<span className="text-[11px] text-zinc-400">{workload.length} people</span>}
            testid="insights-workload" className="lg:col-span-2"
          >
            <div className="space-y-0.5">
              {workload.length === 0 && <p className="text-center text-[12px] text-zinc-400 py-6">No active assignments</p>}
              {workload.map(row => <WorkloadRow key={row.user?.user_id} row={row} max={maxWorkload} />)}
            </div>
            {workload.length > 0 && (
              <div className="flex items-center justify-end gap-4 text-[10px] text-zinc-400 mt-3 pt-3 border-t border-zinc-100">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-200" /> All</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> In progress</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Overdue</span>
              </div>
            )}
          </Section>

          {/* Leaderboard */}
          <Section title="Top contributors" subtitle={`Last ${windowDays} days`} icon={Trophy} testid="insights-leaderboard">
            {leaderboard.rows.length === 0 ? (
              <p className="text-[11px] text-zinc-400 text-center py-6">No completions yet in this window.</p>
            ) : (
              <div className="space-y-1.5">
                {leaderboard.rows.slice(0, 8).map((r, idx) => (
                  <div key={r.user?.user_id || idx} className="flex items-center gap-2 px-1.5 py-1.5 rounded-md hover:bg-zinc-50 transition-colors" data-testid={`leaderboard-row-${idx}`}>
                    <div className="w-6 flex-shrink-0 flex items-center justify-center">
                      {idx === 0 ? <Crown size={16} weight="fill" className="text-amber-500" /> :
                        <span className={`text-[11px] font-black tabular-nums ${idx < 3 ? 'text-zinc-700' : 'text-zinc-400'}`}>#{idx + 1}</span>}
                    </div>
                    <Avatar className="h-7 w-7">
                      {r.user?.avatar && <AvatarImage src={r.user.avatar} />}
                      <AvatarFallback className={`${getAvatarStyle(r.user?.user_id || r.user?.name).bg} text-white text-[9px] font-bold`}>{getInitials(r.user?.name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-[12px] text-zinc-800 truncate flex-1 font-medium">{r.user?.name || 'Unknown'}</span>
                    <span className="text-[12px] font-black text-emerald-600 tabular-nums">{r.completed}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
